import React, { useState, useEffect } from 'react';
import DeliveryTrackingMap from './DeliveryTrackingMap';
import './OrderDetailModal.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';

const formatAddress = (addr) => {
  if (!addr) return '—';
  if (Array.isArray(addr)) return addr.filter(Boolean).join(', ') || '—';
  return String(addr);
};

const PAYMENT_STATUS_META = {
  SETTLED:   { label: 'Paid',     cls: 'paid' },
  REFUNDED:  { label: 'Refunded', cls: 'refunded' },
  FAILED:    { label: 'Failed',   cls: 'failed' },
  REVERSED:  { label: 'Failed',   cls: 'failed' },
  CANCELLED: { label: 'Failed',   cls: 'failed' },
};

function paymentStatusMeta(status) {
  return PAYMENT_STATUS_META[String(status || '').toUpperCase()]
    || { label: 'Unpaid', cls: 'unpaid' };
}

const OrderDetailModal = ({
  order,
  customerName,
  customerEmail,
  customerPhone,
  formatCurrency,
  formatDate,
  onClose,
  onNavigateStore,
  onNavigateCustomer,
}) => {
  const [showDeliveryMap, setShowDeliveryMap] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // raw status string, or 'UNKNOWN'/'NOT_FOUND'

  const orderId = order?.orderId || order?.$id;

  useEffect(() => {
    if (!orderId) { setPaymentStatus(null); return; }
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/payment/status/${orderId}`, {
      headers: { 'X-API-Key': API_KEY },
    })
      .then((res) => res.json())
      .then((data) => { if (!cancelled) setPaymentStatus(data.status || null); })
      .catch(() => { if (!cancelled) setPaymentStatus(null); });
    return () => { cancelled = true; };
  }, [orderId]);

  if (!order) return null;

  const hasDelivery = !!(order.deliveryRequestId || order.driver);

  return (
    <div className="order-detail-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="order-detail-title">
      <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-detail-modal-header">
          <h2 id="order-detail-title">Order {order.orderId || order.$id}</h2>
          <button type="button" className="order-detail-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="order-detail-modal-body">
          <div className="order-detail-modal-section">
            <h3>Order Information</h3>
            <div className="order-detail-modal-grid">
              <div className="order-detail-modal-field">
                <label>Order ID</label>
                <span>{order.orderId || order.$id || '—'}</span>
              </div>
              <div className="order-detail-modal-field">
                <label>Date</label>
                <span>{formatDate(order.orderDate)}</span>
              </div>
              <div className="order-detail-modal-field">
                <label>Status</label>
                <span className={`order-detail-status order-detail-status-${(order.status || '').toLowerCase()}`}>
                  {order.status || '—'}
                </span>
              </div>
              {order.trackingNumber && (
                <div className="order-detail-modal-field">
                  <label>Tracking</label>
                  <span>{order.trackingNumber}</span>
                </div>
              )}
              {order.paymentNumber && (
                <div className="order-detail-modal-field">
                  <label>Payment Ref</label>
                  <span>{order.paymentNumber}</span>
                </div>
              )}
              <div className="order-detail-modal-field">
                <label>Payment</label>
                {(() => {
                  const meta = paymentStatusMeta(paymentStatus);
                  return (
                    <span className={`order-detail-status order-detail-status-${meta.cls}`}>
                      {meta.label}
                    </span>
                  );
                })()}
              </div>
              {order.deliveryFee != null && order.deliveryFee !== '' && (
                <div className="order-detail-modal-field">
                  <label>Delivery Fee</label>
                  <span>{formatCurrency(order.deliveryFee)}</span>
                </div>
              )}
              {order.deliveryMethod && (
                <div className="order-detail-modal-field">
                  <label>Delivery Method</label>
                  <span>{order.deliveryMethod}</span>
                </div>
              )}
            </div>
          </div>

          <div className="order-detail-modal-section">
            <h3>Parties</h3>
            <div className="order-detail-modal-grid">
              <div className="order-detail-modal-field">
                <label>Store</label>
                <span>
                  {onNavigateStore && order.storeId ? (
                    <button type="button" className="order-detail-modal-link" onClick={() => onNavigateStore(order.storeId)}>
                      {order.storeName || order.storeId}
                    </button>
                  ) : (
                    order.storeName || order.storeId || '—'
                  )}
                </span>
              </div>
              <div className="order-detail-modal-field order-detail-modal-field-wide">
                <label>Customer</label>
                <span>
                  {onNavigateCustomer && order.customerId ? (
                    <button type="button" className="order-detail-modal-link" onClick={() => onNavigateCustomer(order.customerId)}>
                      {customerName || order.customerId || '—'}
                    </button>
                  ) : (
                    customerName || order.customerId || '—'
                  )}
                  {customerEmail && <span className="order-detail-modal-meta"> ({customerEmail})</span>}
                </span>
              </div>
              {customerPhone && (
                <div className="order-detail-modal-field">
                  <label>Customer Phone</label>
                  <span>
                    <a href={`tel:${customerPhone}`}>{customerPhone}</a>
                  </span>
                </div>
              )}
              <div className="order-detail-modal-field">
                <label>Driver</label>
                <span>{order.driver?.driverName || order.driver?.driverId || '—'}</span>
              </div>
            </div>
          </div>

          {(order.shippingAddress && (Array.isArray(order.shippingAddress) ? order.shippingAddress.length > 0 : order.shippingAddress)) && (
            <div className="order-detail-modal-section">
              <h3>Shipping Address</h3>
              <p className="order-detail-modal-address">{formatAddress(order.shippingAddress)}</p>
            </div>
          )}

          {hasDelivery && order.deliveryRequestId && (
            <div className="order-detail-modal-section">
              <h3>Delivery</h3>
              <p className="order-detail-modal-desc">This order uses our delivery service.</p>
              <button
                type="button"
                className="order-detail-modal-btn-track"
                onClick={() => setShowDeliveryMap(true)}
              >
                View delivery route
              </button>
            </div>
          )}

          <div className="order-detail-modal-section">
            <h3>Items ({order.items?.length ?? 0})</h3>
            <div className="order-detail-modal-table-wrap">
              <table className="order-detail-modal-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Amount (TZS)</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="order-detail-modal-empty">No items</td>
                    </tr>
                  ) : (
                    (order.items || []).map((item, i) => (
                      <tr key={i}>
                        <td>{item.productName || '—'}</td>
                        <td>{item.quantity ?? '—'}</td>
                        <td>{item.unit || item.packSize || '—'}</td>
                        <td>{formatCurrency(item.totalAmount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}><strong>Total</strong></td>
                    <td><strong>{formatCurrency(order.totalAmount)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showDeliveryMap && order.deliveryRequestId && (
        <DeliveryTrackingMap
          requestId={order.deliveryRequestId}
          onClose={() => setShowDeliveryMap(false)}
        />
      )}
    </div>
  );
};

export default OrderDetailModal;
